import { supabase } from '@/lib/supabase';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

interface MonthlyCalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  onClose?: () => void;
}

const MonthlyCalendar: React.FC<MonthlyCalendarProps> = ({
  selectedDate,
  onDateSelect,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [datesWithData, setDatesWithData] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // 현재 월의 첫 날과 마지막 날 계산
  const getMonthStart = useCallback((date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }, []);

  const getMonthEnd = useCallback((date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }, []);

  // 해당 월에 데이터가 있는 날짜들 가져오기
  useEffect(() => {
    const fetchDatesWithData = async () => {
      setLoading(true);
      try {
        const monthStart = getMonthStart(currentMonth);
        const monthEnd = getMonthEnd(currentMonth);

        // 로컬 시간 기준으로 날짜 문자열 생성
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startDateStr = formatDate(monthStart);
        const endDateStr = formatDate(monthEnd);

        const { data, error } = await supabase
          .from('music_data')
          .select('broadcast_date')
          .gte('broadcast_date', startDateStr)
          .lte('broadcast_date', endDateStr);

        if (error) {
          console.error('Error fetching dates with data:', error);
          return;
        }

        if (data) {
          const uniqueDates = new Set<string>();
          for (const item of data) {
            if (item.broadcast_date) {
              uniqueDates.add(item.broadcast_date);
            }
          }
          setDatesWithData(uniqueDates);
        }
      } catch (error) {
        console.error('Failed to fetch dates with data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDatesWithData();
  }, [currentMonth, getMonthStart, getMonthEnd]);

  // 달력 그리드 생성
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: (Date | null)[] = [];
    const currentDate = new Date(startDate);

    // 6주 * 7일 = 42일
    for (let i = 0; i < 42; i++) {
      if (currentDate.getMonth() === month) {
        days.push(new Date(currentDate));
      } else {
        days.push(null);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    // 로컬 시간 기준으로 날짜 문자열 생성 (UTC 변환 문제 방지)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    onDateSelect(dateStr);
    if (onClose) {
      onClose();
    }
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // 선택된 날짜가 있으면 해당 월로 달력 초기화
  useEffect(() => {
    if (selectedDate) {
      const date = new Date(`${selectedDate}T00:00:00`); // 로컬 시간으로 파싱
      if (!Number.isNaN(date.getTime())) {
        setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    }
  }, [selectedDate]);

  const days = getCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  // 로컬 시간 기준으로 오늘 날짜 문자열 생성
  const todayYear = today.getFullYear();
  const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
  const todayDay = String(today.getDate()).padStart(2, '0');
  const todayStr = `${todayYear}-${todayMonth}-${todayDay}`;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 sm:p-4 w-full max-w-sm mx-auto">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="p-2 hover:bg-gray-800 rounded-md transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5 text-gray-400" />
        </button>

        <h3 className="text-base sm:text-lg font-semibold text-white">
          {formatMonthYear(currentMonth)}
        </h3>

        <button
          type="button"
          onClick={handleNextMonth}
          className="p-2 hover:bg-gray-800 rounded-md transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </button>
      </div>

      {/* Week Days Header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-xs sm:text-sm font-medium text-gray-400 py-1 sm:py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            // For empty cells, use a combination that's stable but unique
            const emptyKey = `empty-${Math.floor(index / 7)}-${index % 7}`;
            return <div key={emptyKey} className="aspect-square" />;
          }

          // 로컬 시간 기준으로 날짜 문자열 생성
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateStr = `${year}-${month}-${day}`;
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          const hasData = datesWithData.has(dateStr);
          const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
          const isClickable = hasData && isCurrentMonth;

          return (
            <button
              type="button"
              key={`date-${dateStr}`}
              onClick={() => {
                if (isClickable) {
                  handleDateClick(date);
                }
              }}
              disabled={!isClickable}
              className={`
                aspect-square rounded-md text-xs sm:text-sm font-medium transition-all relative flex flex-col items-center justify-center
                ${!isCurrentMonth ? 'text-gray-600' : isClickable ? 'text-white' : 'text-gray-500'}
                ${!isClickable ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                ${
                  isSelected && isClickable
                    ? 'bg-blue-600 text-white ring-2 ring-blue-400 scale-105'
                    : isToday && isClickable
                      ? 'bg-gray-800 ring-2 ring-gray-600'
                      : isClickable
                        ? 'hover:bg-gray-800 hover:scale-105'
                        : ''
                }
              `}
            >
              {/* Has data badge - displayed above the date text with spacing */}
              {hasData && isCurrentMonth && (
                <span className="absolute top-1 sm:top-1.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full z-20" />
              )}
              <span
                className={`relative z-10 ${hasData && isCurrentMonth ? 'pt-2 sm:pt-2.5' : ''}`}
              >
                {date.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-700 flex items-center justify-center gap-3 sm:gap-4 text-xs text-gray-400">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-blue-400" />
          <span className="text-xs">Has data</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-md bg-blue-600" />
          <span className="text-xs">Selected</span>
        </div>
      </div>
    </div>
  );
};

export default MonthlyCalendar;
